<template>
  <InfuseModal :title="t('title')" @close="emit('close')">
    <form id="send-trigger-message" @submit.prevent="onSubmit">
      <InfuseField size="small" :label="t('messageType.label')">
        <InfuseSelect
          v-model="selectedMessageType"
          name="messageType"
          :options="availableMessageTypes"
          :format="(val: string) => t(`messageType.${val}`)"
          :required="true"
        />
      </InfuseField>

      <InfuseField
        v-if="selectedMessageType === MessageTrigger.CUSTOM_TRIGGER"
        size="small"
        :label="t('customTrigger.label')"
        :help-text="t('customTrigger.help')"
      >
        <InfuseSelect v-model="selectedCustomTrigger" name="customTrigger" :options="customTriggers" :required="true" />
      </InfuseField>
    </form>

    <template #actions>
      <InfuseButton
        data-testid="modal-cancel-button"
        color="secondary"
        type="button"
        form="send-trigger-message"
        @click="emit('close')"
      >
        {{ t('action.cancel') }}
      </InfuseButton>

      <InfuseButton
        data-testid="modal-confirm-button"
        type="submit"
        form="send-trigger-message"
        :disabled="!selectedMessageType"
      >
        {{ t('action.send') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { MessageTrigger } from '@infuse/api/cpms/operator';
import { Protocol } from '@infuse/cpms/charging-station/store';

import { InfuseButton, InfuseField, InfuseModal, InfuseSelect } from '@infuse/ui/components';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  connectorId?: number;
  evseId?: number;
  ocppVersion: Protocol;
}

interface Emits {
  (event: 'close'): void;
  (
    event: 'send',
    evseId: number | undefined,
    connectorId: number | undefined,
    messageType: MessageTrigger,
    customTrigger?: string
  ): void;
}

const props = withDefaults(defineProps<Props>(), {
  ocppVersion: Protocol.OCPPJ16
});
const emit = defineEmits<Emits>();

const { t } = useI18n();

const cs16Types: MessageTrigger[] = [
  MessageTrigger.BOOT_NOTIFICATION,
  MessageTrigger.DIAGNOSTICS_STATUS_NOTIFICATION,
  MessageTrigger.FIRMWARE_STATUS_NOTIFICATION,
  MessageTrigger.HEARTBEAT,
  MessageTrigger.METER_VALUES,
  MessageTrigger.STATUS_NOTIFICATION
];

const cs201Types: MessageTrigger[] = [
  MessageTrigger.BOOT_NOTIFICATION,
  MessageTrigger.LOG_STATUS_NOTIFICATION,
  MessageTrigger.FIRMWARE_STATUS_NOTIFICATION,
  MessageTrigger.HEARTBEAT,
  MessageTrigger.METER_VALUES,
  MessageTrigger.SIGN_CHARGING_STATION_CERTIFICATE,
  MessageTrigger.SIGN_COMBINED_CERTIFICATE,
  MessageTrigger.SIGN_V2G_CERTIFICATE,
  MessageTrigger.TRANSACTION_EVENT,
  MessageTrigger.PUBLISH_FIRMWARE_STATUS_NOTIFICATION
];

const cs21Types: MessageTrigger[] = [
  MessageTrigger.BOOT_NOTIFICATION,
  MessageTrigger.LOG_STATUS_NOTIFICATION,
  MessageTrigger.FIRMWARE_STATUS_NOTIFICATION,
  MessageTrigger.HEARTBEAT,
  MessageTrigger.METER_VALUES,
  MessageTrigger.SIGN_CHARGING_STATION_CERTIFICATE,
  MessageTrigger.SIGN_V2G_CERTIFICATE,
  MessageTrigger.SIGN_V2G20_CERTIFICATE,
  MessageTrigger.TRANSACTION_EVENT,
  MessageTrigger.SIGN_COMBINED_CERTIFICATE,
  MessageTrigger.PUBLISH_FIRMWARE_STATUS_NOTIFICATION,
  MessageTrigger.CUSTOM_TRIGGER
];

const customTriggers: string[] = [
  'Authorize',
  'BatterySwap',
  'ClearedChargingLimit',
  'ClosePeriodicEventStream',
  'DataTransfer',
  'Get15118EVCertificate',
  'GetCertificateStatus',
  'NotifyChargingLimit',
  'NotifyCustomerInformation',
  'NotifyDERAlarm',
  'NotifyDERStartStop',
  'NotifyDisplayMessages',
  'NotifyEVChargingNeeds',
  'NotifyEVChargingSchedule',
  'NotifyEvent',
  'NotifyMonitoringReport',
  'NotifyPeriodicEventStream',
  'NotifyPriorityCharging',
  'NotifyReport',
  'NotifySettlement',
  'OpenPeriodicEventStream',
  'PullDynamicScheduleUpdate',
  'ReportChargingProfiles',
  'ReservationStatusUpdate',
  'SecurityEventNotification',
  'SignCertificate',
  'VATNumberValidation'
];

const connectorTypes: MessageTrigger[] = [MessageTrigger.METER_VALUES, MessageTrigger.STATUS_NOTIFICATION];

const availableMessageTypes: MessageTrigger[] = !props.evseId
  ? props.ocppVersion === Protocol.OCPPJ16
    ? cs16Types
    : props.ocppVersion === Protocol.OCPP201
    ? cs201Types
    : cs21Types
  : connectorTypes;

const selectedCustomTrigger = ref<string | undefined>();
const selectedMessageType = ref<MessageTrigger>();

function onSubmit() {
  if (selectedMessageType.value) {
    emit(
      'send',
      props.evseId,
      props.connectorId,
      selectedMessageType.value,
      selectedMessageType.value === MessageTrigger.CUSTOM_TRIGGER ? selectedCustomTrigger.value : undefined
    );
  }
}
</script>

<i18n>
{
  "en": {
    "title": "Request message",
    "action": {
      "cancel": "Cancel",
      "send": "Request message"
    },
    "messageType": {
      "label": "Message type",
      "select": "Select a message type...",
      "BOOT_NOTIFICATION": "Boot notification",
      "LOG_STATUS_NOTIFICATION": "Log status notification",
      "DIAGNOSTICS_STATUS_NOTIFICATION": "Diagnostics status notification",
      "FIRMWARE_STATUS_NOTIFICATION": "Firmware status notification",
      "HEARTBEAT": "Heartbeat",
      "METER_VALUES": "Meter values",
      "SIGN_CHARGING_STATION_CERTIFICATE": "Sign charging station certificate",
      "SIGN_V_2_G_CERTIFICATE": "Sign V2G certificate",
      "SIGN_V_2_G_20_CERTIFICATE": "Sign V2G-20 certificate",
      "STATUS_NOTIFICATION": "Status notification",
      "TRANSACTION_EVENT": "Transaction event",
      "SIGN_COMBINED_CERTIFICATE": "Sign combined certificate",
      "PUBLISH_FIRMWARE_STATUS_NOTIFICATION": "Publish firmware status notification",
      "CUSTOM_TRIGGER": "Custom message type"
    },
    "customTrigger": {
      "label": "Custom message type",
      "help": "Note: not all of these are guaranteed to be accepted by the charging station."
    }
  },
  "nl": {
    "title": "Bericht aanvragen",
    "action": {
      "cancel": "Annuleren",
      "send": "Verstuur berichtverzoek"
    },
    "messageType": {
      "label": "Berichttype",
      "select": "Selecteer een berichttype...",
      "BOOT_NOTIFICATION": "Bootnotificatie",
      "LOG_STATUS_NOTIFICATION": "Statusnotificatie voor logging",
      "DIAGNOSTICS_STATUS_NOTIFICATION": "Statusnotificatie voor diagnostieken",
      "FIRMWARE_STATUS_NOTIFICATION": "Statusnotificatie voor firmware",
      "HEARTBEAT": "Hartslag",
      "METER_VALUES": "Meterwaarden",
      "SIGN_CHARGING_STATION_CERTIFICATE": "Laadpaalcertificaat ondertekenen",
      "SIGN_V_2_G_CERTIFICATE": "V2G-certificaat ondertekenen",
      "SIGN_V_2_G_20_CERTIFICATE": "V2G-20-certificaat ondertekenen",
      "STATUS_NOTIFICATION": "Statusnotificatie",
      "TRANSACTION_EVENT": "Transactie-event",
      "SIGN_COMBINED_CERTIFICATE": "Gecombineerd certificaat ondertekenen",
      "PUBLISH_FIRMWARE_STATUS_NOTIFICATION": "Statusnotificatie voor firmware publicatie",
      "CUSTOM_TRIGGER": "Ander berichttype"
    },
    "customTrigger": {
      "label": "Ander berichttype",
      "help": "Let op: het kan zijn dat de laadpaal niet al deze waarden ondersteunt."
    }
  }
}
</i18n>
